/**
 * Sanitizes filename for S3 compatibility
 * - Uses only S3-safe characters: A-Z, a-z, 0-9, -, _, .
 * - Collapses multiple consecutive replacement characters
 * - Preserves file extension
 * - Handles edge cases like leading/trailing unsafe chars
 * @param filename - The original filename
 * @param usePermissive - If true, uses more permissive regex that preserves more characters
 */
export const sanitizeFilenameForS3 = (
  filename: string,
  usePermissive = false
): string => {
  // AWS S3 safe characters: A-Z, a-z, 0-9, -, _, ., *
  // This regex matches characters that are NOT safe for S3 object keys
  const s3UnsafeCharactersRegex = /[^A-Za-z0-9._-]/g;

  //More permissive regex that only replaces truly problematic characters
  const problematicCharactersRegex = /[\s!@#$%^&*()+={}[\]|\\:";'<>?,/`~]/g;

  const replaceChar = "_";
  const regex = usePermissive
    ? problematicCharactersRegex
    : s3UnsafeCharactersRegex;

  const lastDotIndex = filename.lastIndexOf(".");
  const name =
    lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
  const extension = lastDotIndex > 0 ? filename.substring(lastDotIndex) : "";

  let sanitizedName = name
    .replace(regex, replaceChar)
    // Collapse multiple consecutive underscores
    .replace(/_{2,}/g, "_")
    // Remove leading/trailing underscores
    .replace(/^_+|_+$/g, "");

  // Ensure we have a valid name
  if (!sanitizedName) {
    sanitizedName = "file";
  }

  // Sanitize extension (keep the dot, sanitize the rest)
  const sanitizedExtension = extension
    ? "." + extension.substring(1).replace(regex, "")
    : "";

  return sanitizedName + sanitizedExtension;
};

export function sanitizeForOpenSearch(
  value: string | null | undefined
): string {
  if (!value) return "";

  return (
    value
      // Normalize whitespace (tabs, newlines, multiple spaces)
      .replace(/\s+/g, " ")

      // Trim leading/trailing whitespace
      .trim()

      // Remove null bytes that can cause issues
      .replace(/\0/g, "")

      // Normalize unicode (NFC normalization)
      .normalize("NFC")
  );
}
